import type { ActionItemStatus, ActionItemCategory } from '../types';
import i18n from '../i18n';

// Single source of truth for action-item status vocabulary on the frontend (supervision revamp
// §3d). The backend normalizes legacy statuses on read, but we canonicalize defensively here too so
// cached/optimistic rows and the (parked) meeting flow render consistently.

/** Statuses that count as "open work" — drives OpenCount + the default "Open" view. */
export const OPEN_STATUSES: ActionItemStatus[] = ['open', 'in-progress'];

/** Canonical statuses selectable in the edit dialog / bulk "Set status" menu. */
export const CANONICAL_STATUSES: ActionItemStatus[] = [
  'open',
  'in-progress',
  'waiting-on-client',
  'ready-for-review',
  'completed',
  'cancelled',
  'carried-forward',
];

const LEGACY_STATUS_MAP: Partial<Record<string, ActionItemStatus>> = {
  identified: 'open',
  'assigned-to-meeting': 'open',
  blocked: 'waiting-on-client',
};

/** Collapse a stored/legacy status to its canonical form. */
export function canonicalizeStatus(status: string | null | undefined): ActionItemStatus {
  if (!status) return 'open';
  const s = status.trim().toLowerCase();
  return LEGACY_STATUS_MAP[s] ?? (s as ActionItemStatus);
}

/** True when the item is open work (open or in-progress) after canonicalization. */
export function isOpenStatus(status: string | null | undefined): boolean {
  return OPEN_STATUSES.includes(canonicalizeStatus(status));
}

/**
 * A record whose every value is a GETTER over an i18n key (Spanish UI). These maps are module-level,
 * and readers enumerate them once (the Action items column catalog builds its filter options from
 * them), so a plain string here would keep the language it was first read in.
 */
function i18nRecord<K extends string>(keys: Record<K, string>): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const [k, key] of Object.entries(keys) as [K, string][]) {
    Object.defineProperty(out, k, { enumerable: true, get: () => i18n.t(key) });
  }
  return out;
}

export const STATUS_LABELS: Record<ActionItemStatus, string> = i18nRecord<ActionItemStatus>({
  open: 'actionItems.status.open',
  'in-progress': 'actionItems.status.inProgress',
  'waiting-on-client': 'actionItems.status.waitingOnClient',
  'ready-for-review': 'actionItems.status.readyForReview',
  completed: 'actionItems.status.completed',
  cancelled: 'actionItems.status.cancelled',
  'carried-forward': 'actionItems.status.carriedForward',
  // Legacy — shown only if an un-normalized value ever reaches the UI.
  identified: 'actionItems.status.open',
  'assigned-to-meeting': 'actionItems.status.open',
  blocked: 'actionItems.status.waitingOnClient',
});

/** A status as a whole label in the UI language (canonicalised first); unknown values as stored. */
export function statusLabel(status: string): string {
  return (STATUS_LABELS as Record<string, string>)[canonicalizeStatus(status)] ?? status;
}

/** Canonical category set (supervision revamp §3d). */
export const CATEGORY_LABELS: Record<ActionItemCategory, string> = i18nRecord<ActionItemCategory>({
  reconciliation: 'actionItems.category.reconciliation',
  'data-entry': 'actionItems.category.dataEntry',
  'missing-documents': 'actionItems.category.missingDocuments',
  'client-communication': 'actionItems.category.clientCommunication',
  'review-finding': 'actionItems.category.reviewFinding',
  process: 'actionItems.category.process',
  training: 'actionItems.category.training',
  other: 'actionItems.category.other',
});

export function getCategoryLabel(category: string): string {
  return (CATEGORY_LABELS as Record<string, string>)[category] ?? category;
}
